import React from 'react';

// The (br) marker in the builder string flags an optional line break. We use
// (br) instead of an <br> tag because HTML characters get stripped from the
// banner text.
//
// When showLineBreak is false (copy without a (br)) the marker is collapsed to a
// space and the copy stays on one line with any colon intact.
//
// When showLineBreak is true (a (br) is present) we render BOTH the break and
// the separator colon into the DOM and let CSS pick per viewport — the break
// must only happen on phones, not tablets or desktop:
//   - the <br> uses `d-md-none`, so it breaks below 768px (phones) and is
//     suppressed at >=768px (tablets/desktop), keeping them on one line
//   - the separator colon uses `d-none d-md-inline`, the inverse — hidden on
//     phones (the break replaces it) and shown at >=768px
// Incidental colons (e.g. a time like 9:00) are mid-line, not a trailing
// separator, so they are never toggled and always render.
export const formatBannerSegment = (text, showLineBreak) => {
    if (!text) return text;

    if (!showLineBreak) {
        return text.replace(/\s*\(br\)\s*/gi, ' ').trim();
    }

    const lines = text.split(/\s*\(br\)\s*/i);
    return lines.map((line, index) => {
        const isLastLine = index === lines.length - 1;
        const hasTrailingColon = /\s*:\s*$/.test(line);
        return (
            <React.Fragment key={index}>
                {line.replace(/\s*:\s*$/, '')}
                {hasTrailingColon && <span className="d-none d-md-inline">: </span>}
                {/* Space keeps the two lines separated once the <br> is hidden on
                    tablet; it collapses with the colon's space when both render. */}
                {!isLastLine && (
                    <>
                        {' '}
                        <br className="d-md-none" />
                    </>
                )}
            </React.Fragment>
        );
    });
};

// Splits banner copy on the "|" separator into the bold first part (rendered as
// the link/lead text) and the regular second part.
export const splitByPipe = (text) => {
    if (text && typeof text !== 'string') {
        throw new Error('Input must be a string');
    } else {
        const bannerTextSplitArray = text?.split('|')?.map((part) => part.trim());
        const specialsBannerLinkTextFirstPart = bannerTextSplitArray[0];
        const specialsBannerTextSecondPart = bannerTextSplitArray[1];

        return { specialsBannerLinkTextFirstPart, specialsBannerTextSecondPart };
    }
};

// A break is only ever possible when the copy contains a (br) marker; CSS then
// decides per viewport whether it actually renders (see formatBannerSegment).
export const hasLineBreakMarker = (text) => /\(br\)/i.test(text ?? '');
